import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format, parse } from "date-fns";
import * as z from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@/context/auth";
import { useBranch } from "@/context/branch";
import { useToast } from "@/hooks/use-toast";
import { formatCurrency } from "@/lib/currency";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Plus, Wallet, Check, AlertTriangle, Banknote, Calendar } from "lucide-react";

// --- API Helpers ---
const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

async function fetchApi<T = unknown>(path: string, opts?: RequestInit): Promise<T> {
  const response = await fetch(`${BASE}${path}`, { credentials: "include", ...opts });
  if (!response.ok) {
    const errData = await response.json().catch(() => ({}));
    const error = new Error(errData.error || `Error ${response.status}`);
    (error as any).status = response.status;
    throw error;
  }
  return response.json();
}

// --- Types ---
type RunStatus = "draft" | "approved" | "processing" | "paid" | "partial" | "failed" | "manual_review" | "unknown" | "reversed" | "submitted";
type ItemStatus = "pending" | "submitting" | "processing" | "submitted" | "awaiting_otp" | "unknown" | "success" | "failed" | "reversed" | "manual_review";

type PayrollRun = {
  id: number;
  period: string;
  branchId: number | null;
  status: RunStatus;
  totalKobo: number;
  createdAt: string;
};

type PayrollItem = {
  id: number;
  staffId: number;
  grossKobo: number;
  deductionsKobo: number;
  netKobo: number;
  staffName: string;
  status: ItemStatus;
  failureReason?: string;
  reference?: string;
  paidAt?: string;
  bankReady: boolean;
  accountLast4?: string;
  bankName?: string;
};

type RunWithItems = PayrollRun & { items: PayrollItem[] };

type PayrollStaff = {
  id: number;
  name: string;
  role: string;
  branchId?: number;
  isActive: boolean;
  bankCode?: string;
  accountLast4?: string;
  bankName?: string;
  basicSalary?: number;
  bankReady: boolean;
};

type Bank = { name: string; code: string; slug: string; id: number };

// --- Schemas ---
const bankFormSchema = z.object({
  bankCode: z.string().min(1, "Bank is required"),
  accountNumber: z.string().refine(val => val === "" || /^\d{10}$/.test(val), "Account number must be 10 digits"),
  basicSalaryNaira: z.coerce.number().min(0, "Salary cannot be negative"),
});

// --- Components ---
function ItemStatusBadge({ status }: { status: ItemStatus }) {
  switch(status) {
    case "success": return <Badge variant="default" className="bg-green-600 hover:bg-green-700 text-white">Paid</Badge>;
    case "failed":
    case "reversed": return <Badge variant="destructive" className="capitalize">{status}</Badge>;
    case "manual_review": return <Badge variant="destructive" className="bg-amber-600 hover:bg-amber-700 text-white">Manual Review</Badge>;
    case "pending": return <Badge variant="secondary" className="bg-muted text-muted-foreground">Pending</Badge>;
    case "submitted":
    case "submitting":
    case "processing": return <Badge variant="secondary" className="bg-blue-100 text-blue-700 hover:bg-blue-200 capitalize">{status}</Badge>;
    case "awaiting_otp": return <Badge variant="secondary" className="bg-indigo-100 text-indigo-700 hover:bg-indigo-200 capitalize">Awaiting OTP</Badge>;
    case "unknown": return <Badge variant="secondary" className="bg-gray-200 text-gray-700 capitalize">Unknown</Badge>;
    default: return <Badge variant="outline" className="capitalize">{String(status).replace("_", " ")}</Badge>;
  }
}

function RunStatusBadge({ status }: { status: RunStatus }) {
  switch(status) {
    case "paid": return <Badge variant="default" className="bg-green-600 hover:bg-green-700 text-white">Paid</Badge>;
    case "approved": return <Badge variant="secondary" className="bg-blue-100 text-blue-700 hover:bg-blue-200">Approved</Badge>;
    case "failed":
    case "reversed": return <Badge variant="destructive" className="capitalize">{status}</Badge>;
    case "partial": return <Badge variant="outline" className="border-amber-400 text-amber-700">Partial</Badge>;
    case "manual_review": return <Badge variant="destructive" className="bg-amber-600 hover:bg-amber-700 text-white">Manual Review</Badge>;
    case "draft": return <Badge variant="outline" className="text-muted-foreground">Draft</Badge>;
    case "processing":
    case "submitted": return <Badge variant="secondary" className="bg-blue-100 text-blue-700 hover:bg-blue-200 capitalize">{status}</Badge>;
    case "unknown": return <Badge variant="secondary" className="bg-gray-200 text-gray-700 capitalize">Unknown</Badge>;
    default: return <Badge variant="outline" className="capitalize">{String(status).replace("_", " ")}</Badge>;
  }
}

export default function Payroll() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "superadmin";

  return (
    <div className="space-y-6 max-w-6xl mx-auto pb-10">
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <Wallet className="w-8 h-8 text-primary" />
          Payroll
        </h1>
        <p className="text-muted-foreground mt-1">
          {isAdmin ? "Manage staff salaries, approve payroll, generate payslips, and export payment schedules." : "View your payslips and payroll history."}
        </p>
      </div>

      <Tabs defaultValue="runs" className="space-y-4">
        <TabsList>
          <TabsTrigger value="runs">Payroll Runs</TabsTrigger>
          {isAdmin && <TabsTrigger value="settings">Salary & Banks</TabsTrigger>}
        </TabsList>
        <TabsContent value="runs" className="space-y-4">
          <RunsTab isAdmin={isAdmin} isStaff={user?.role === "staff" || user?.role === "manager"} />
        </TabsContent>
        {isAdmin && (
          <TabsContent value="settings" className="space-y-4">
            <SalaryBanksTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Runs Tab
// -----------------------------------------------------------------------------
function RunsTab({ isAdmin, isStaff }: { isAdmin: boolean; isStaff: boolean }) {
  const [period, setPeriod] = useState(format(new Date(), "yyyy-MM"));
  const { activeBranchId } = useBranch();

  const queryParams = new URLSearchParams();
  if (period) queryParams.set("period", period);
  if (isAdmin && activeBranchId) queryParams.set("branchId", String(activeBranchId));

  const { data, isLoading } = useQuery({
    queryKey: ["payroll-runs", queryParams.toString()],
    queryFn: () => fetchApi<{runs: PayrollRun[]}>(`/api/payroll/runs?${queryParams.toString()}`)
  });

  const runs = data?.runs || [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between border-b pb-4 mb-4">
        <div>
          <CardTitle>Payroll History</CardTitle>
          <CardDescription>Review and manage payroll periods.</CardDescription>
        </div>
        <div className="flex gap-3">
          <div className="flex items-center border rounded-md px-3 bg-background">
            <Calendar className="w-4 h-4 text-muted-foreground mr-2" />
            <input
              type="month"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="bg-transparent border-none text-sm outline-none w-[120px] focus:ring-0 py-1.5"
            />
          </div>
          {isAdmin && <CreateRunDialog currentPeriod={period} activeBranchId={activeBranchId} />}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : runs.length === 0 ? (
          <div className="py-12 text-center text-muted-foreground border border-dashed rounded-lg bg-muted/20">
            <Banknote className="w-8 h-8 mx-auto mb-3 opacity-50" />
            <p className="font-medium text-foreground">No payroll runs found.</p>
            <p className="text-sm mt-1">
              {isAdmin ? `Create a draft to get started for ${period}.` : "No payslips available for this period."}
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Period</TableHead>
                {!isStaff && <TableHead>Total Amount</TableHead>}
                <TableHead>Status</TableHead>
                <TableHead>Date Created</TableHead>
                <TableHead className="w-[100px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.map((run) => (
                <TableRow key={run.id}>
                  <TableCell className="font-medium">
                    {(() => {
                      const parsed = parse(run.period, "yyyy-MM", new Date());
                      return Number.isNaN(parsed.getTime()) ? run.period : format(parsed, "MMMM yyyy");
                    })()}
                  </TableCell>
                  {!isStaff && <TableCell>{formatCurrency(run.totalKobo / 100)}</TableCell>}
                  <TableCell>
                    <RunStatusBadge status={run.status} />
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {(() => {
                      const parsed = new Date(run.createdAt);
                      return Number.isNaN(parsed.getTime()) ? run.createdAt : format(parsed, "MMM d, yyyy");
                    })()}
                  </TableCell>
                  <TableCell className="text-right">
                    <ViewRunDialog runId={run.id} isAdmin={isAdmin} isStaff={isStaff} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Action Components
// -----------------------------------------------------------------------------
function ExportAction({ run }: { run: RunWithItems }) {
  const handleExport = () => {
    const headers = ["Payroll Period", "Staff Name", "Bank Name", "Masked Account", "Gross Pay", "Deductions", "Net Pay", "Status"];

    const rows = run.items.map(item => [
      run.period,
      item.staffName,
      item.bankName || "N/A",
      item.accountLast4 ? `****${item.accountLast4}` : "N/A",
      (item.grossKobo / 100).toFixed(2),
      (item.deductionsKobo / 100).toFixed(2),
      (item.netKobo / 100).toFixed(2),
      item.status
    ]);

    const title = ["Internal schedule — account numbers are masked."];

    const escapeCsv = (val: string | number) => `"${String(val).replace(/"/g, '""')}"`;

    const csvContent = [
      title.map(escapeCsv).join(","),
      headers.map(escapeCsv).join(","),
      ...rows.map(row => row.map(escapeCsv).join(","))
    ].join("\r\n");

    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `retailpulse-payroll-${run.period}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Button variant="outline" className="gap-2 w-full sm:w-auto" onClick={handleExport}>
      <Banknote className="w-4 h-4" /> Export Payment Schedule
    </Button>
  );
}

function PrintPayslipAction({ item, run, store }: { item: PayrollItem, run: RunWithItems, store: any }) {
  const { toast } = useToast();

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast({ title: "Popup blocked", description: "Please allow popups to print payslips", variant: "destructive" });
      return;
    }

    const escapeHtml = (unsafe: string | number) => {
      return String(unsafe)
           .replace(/&/g, "&amp;")
           .replace(/</g, "&lt;")
           .replace(/>/g, "&gt;")
           .replace(/"/g, "&quot;")
           .replace(/'/g, "&#039;");
    };

    const storeName = escapeHtml(store?.name || "RetailPulse");
    const address = escapeHtml(store?.address || "");
    const staffName = escapeHtml(item.staffName);
    const gross = escapeHtml(formatCurrency(item.grossKobo / 100));
    const deductions = escapeHtml(formatCurrency(item.deductionsKobo / 100));
    const net = escapeHtml(formatCurrency(item.netKobo / 100));
    const status = escapeHtml(item.status);
    const genDate = escapeHtml(format(new Date(), "PPpp"));
    const periodFormatted = escapeHtml((() => {
       const parsed = parse(run.period, "yyyy-MM", new Date());
       return Number.isNaN(parsed.getTime()) ? run.period : format(parsed, "MMMM yyyy");
    })());

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Payslip - ${staffName} - ${periodFormatted}</title>
          <style>
            body { font-family: system-ui, sans-serif; padding: 40px; max-width: 600px; margin: 0 auto; color: #111; }
            h1 { text-align: center; margin-bottom: 5px; font-size: 24px; }
            h2 { text-align: center; color: #555; font-size: 16px; margin-top: 0; margin-bottom: 40px; font-weight: normal; }
            .row { display: flex; justify-content: space-between; border-bottom: 1px solid #eaeaea; padding: 12px 0; }
            .row:last-child { border-bottom: none; }
            .total { font-weight: 600; font-size: 18px; border-top: 2px solid #111; margin-top: 10px; padding-top: 15px; }
            .footer { margin-top: 60px; text-align: center; font-size: 12px; color: #888; }
            .address { text-align: center; color: #666; margin-bottom: 30px; font-size: 14px; }
            .status { text-transform: capitalize; }
          </style>
        </head>
        <body>
          <h1>${storeName}</h1>
          ${address ? `<div class="address">${address}</div>` : ''}
          <h2>Payslip</h2>

          <div class="row"><span>Employee</span><strong>${staffName}</strong></div>
          <div class="row"><span>Period</span><strong>${periodFormatted}</strong></div>
          <div class="row"><span>Status</span><strong class="status">${status}</strong></div>

          <div style="margin-top: 40px;">
            <div class="row"><span>Gross Pay</span><span>${gross}</span></div>
            <div class="row"><span>Deductions</span><span>${deductions}</span></div>
            <div class="row total"><span>Net Pay</span><span>${net}</span></div>
          </div>

          <div class="footer">Generated on ${genDate}</div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  return (
    <Button variant="ghost" size="sm" onClick={handlePrint} className="h-8 text-xs">
      Print
    </Button>
  );
}

// -----------------------------------------------------------------------------
// View Run Dialog
// -----------------------------------------------------------------------------
function ViewRunDialog({ runId, isAdmin, isStaff }: { runId: number; isAdmin: boolean; isStaff: boolean }) {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: run, isLoading } = useQuery({
    queryKey: ["payroll-run", runId],
    queryFn: () => fetchApi<RunWithItems>(`/api/payroll/runs/${runId}`),
    enabled: open
  });

  const approveMut = useMutation({
    mutationFn: () => fetchApi(`/api/payroll/runs/${runId}/approve`, { method: "POST" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["payroll-run", runId] });
      queryClient.invalidateQueries({ queryKey: ["payroll-runs"] });
      toast({ title: "Payroll approved" });
    },
    onError: (err: Error) => toast({ title: "Approval failed", description: err.message, variant: "destructive" }),
  });

  const isDraft = run?.status === "draft";
  const isApproved = run?.status === "approved";
  const isPaid = run?.status === "paid";
  const { store } = useAuth();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm">View</Button>
      </DialogTrigger>
      <DialogContent className="max-w-5xl max-h-[85vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <DialogTitle className="text-xl">
                {run ? (() => {
                   const parsed = parse(run.period, "yyyy-MM", new Date());
                   return Number.isNaN(parsed.getTime()) ? run.period : format(parsed, "MMMM yyyy");
                })() : "Loading..."} Payroll
              </DialogTitle>
              {run && !isStaff && (
                <DialogDescription className="mt-1">
                  Total amount: {formatCurrency(run.totalKobo / 100)}
                </DialogDescription>
              )}
            </div>
            {run && (
              <div>
                <RunStatusBadge status={run.status} />
              </div>
            )}
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 bg-muted/10">
          {isLoading || !run ? (
            <div className="py-12 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
          ) : (
            <div className="space-y-4">
              {isAdmin && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-md text-blue-800 flex items-start gap-3">
                  <Banknote className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <h4 className="font-semibold">Manual Bank Payments</h4>
                    <p className="mt-1">
                      Approving a payroll run locks the payment schedule. RetailPulse does not automatically move funds. You can export the schedule to process bank transfers externally.
                    </p>
                  </div>
                </div>
              )}

              <div className="rounded-md border bg-background">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Staff Member</TableHead>
                      <TableHead className="text-right">Gross Pay</TableHead>
                      <TableHead className="text-right text-destructive">Deductions</TableHead>
                      <TableHead className="text-right">Net Pay</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Bank</TableHead>
                      <TableHead className="w-[120px] text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {run.items.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <div className="font-medium">{item.staffName}</div>
                          {item.reference && <div className="text-[10px] text-muted-foreground font-mono mt-1">Ref: {item.reference}</div>}
                        </TableCell>
                        <TableCell className="text-right">{formatCurrency(item.grossKobo / 100)}</TableCell>
                        <TableCell className="text-right text-destructive">{item.deductionsKobo > 0 ? "-" + formatCurrency(item.deductionsKobo / 100) : "—"}</TableCell>
                        <TableCell className="text-right font-semibold">{formatCurrency(item.netKobo / 100)}</TableCell>
                        <TableCell>
                          <div className="flex flex-col items-start">
                            <ItemStatusBadge status={item.status} />
                            {item.failureReason && (
                              <span className="text-[10px] text-destructive max-w-[160px] leading-tight block mt-1 break-words" title={item.failureReason}>
                                {item.failureReason}
                              </span>
                            )}
                            {item.paidAt && (
                              <span className="text-[10px] text-muted-foreground mt-1 block">
                                Paid: {format(new Date(item.paidAt), "MMM d, yyyy h:mm a")}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {item.bankReady ? (
                            <div className="text-xs">
                              <div className="font-medium truncate max-w-[120px]" title={item.bankName}>{item.bankName}</div>
                              <div className="text-muted-foreground">****{item.accountLast4}</div>
                            </div>
                          ) : (
                            <span className="text-xs text-amber-600 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" /> Setup required
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <PrintPayslipAction item={item} run={run} store={store} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>

        {isAdmin && run && (
          <DialogFooter className="px-6 py-4 border-t bg-background flex flex-col sm:flex-row gap-2 justify-end shrink-0">
            {isDraft && (
              <Button onClick={() => approveMut.mutate()} disabled={approveMut.isPending} className="w-full sm:w-auto">
                {approveMut.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                Approve Run
              </Button>
            )}

            {(isApproved || isPaid) && (
              <ExportAction run={run} />
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// Create Run Dialog
// -----------------------------------------------------------------------------
function CreateRunDialog({ currentPeriod, activeBranchId }: { currentPeriod: string; activeBranchId: number | null }) {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: staffData, isLoading: isLoadingStaff } = useQuery({
    queryKey: ["payroll-staff"],
    queryFn: () => fetchApi<{ staff: PayrollStaff[] }>("/api/payroll/staff"),
    enabled: open,
  });
  const staffList = staffData?.staff ?? [];

  const [items, setItems] = useState<Record<number, { gross: string, deductions: string }>>({});

  const activeStaff = useMemo(() => {
    return (staffList || []).filter(s =>
      s.isActive && (activeBranchId === null || s.branchId === activeBranchId)
    );
  }, [staffList, activeBranchId]);

  const handleOpen = (val: boolean) => {
    if (val && staffList && staffList.length > 0) {
      const initial: Record<number, { gross: string, deductions: string }> = {};
      staffList.forEach(s => {
        if (s.isActive && (activeBranchId === null || s.branchId === activeBranchId)) {
          initial[s.id] = {
            gross: s.basicSalary ? (s.basicSalary / 100).toString() : "",
            deductions: ""
          };
        }
      });
      setItems(initial);
    }
    setOpen(val);
  };

  useMemo(() => {
    if (open && activeStaff.length > 0 && Object.keys(items).length === 0) {
      const initial: Record<number, { gross: string, deductions: string }> = {};
      activeStaff.forEach(s => {
        initial[s.id] = {
          gross: s.basicSalary ? (s.basicSalary / 100).toString() : "",
          deductions: ""
        };
      });
      setItems(initial);
    }
  }, [open, activeStaff, items]);

  const createMut = useMutation({
    mutationFn: (payload: any) => fetchApi("/api/payroll/runs", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["payroll-runs"] });
      toast({ title: "Draft payroll created" });
      setOpen(false);
    },
    onError: (err: Error) => toast({ title: "Failed to create run", description: err.message, variant: "destructive" }),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentPeriod) {
      toast({ title: "Please select a period", variant: "destructive" });
      return;
    }
    const payloadItems = Object.entries(items).map(([staffId, vals]) => {
      const gross = Math.round(Number(vals.gross || 0) * 100);
      const ded = Math.round(Number(vals.deductions || 0) * 100);
      return { staffId: Number(staffId), grossKobo: gross, deductionsKobo: ded };
    }).filter(i => i.grossKobo > 0);

    if (payloadItems.length === 0) {
      toast({ title: "No items", description: "Enter gross pay for at least one staff member.", variant: "destructive" });
      return;
    }

    createMut.mutate({
      period: currentPeriod,
      branchId: activeBranchId || undefined,
      items: payloadItems
    });
  };

  const totalPreview = useMemo(() => {
    return Object.values(items).reduce((acc, curr) => acc + (Number(curr.gross || 0) - Number(curr.deductions || 0)), 0);
  }, [items]);

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2"><Plus className="w-4 h-4" /> Create Draft</Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col p-0">
        <form onSubmit={onSubmit} className="flex flex-col h-full overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
            <DialogTitle>
              New Payroll Run — {
                (() => {
                  const parsed = parse(currentPeriod, "yyyy-MM", new Date());
                  return Number.isNaN(parsed.getTime()) ? currentPeriod : format(parsed, "MMMM yyyy");
                })()
              }
            </DialogTitle>
            <DialogDescription>
              Enter gross pay and deductions for each active staff member. Staff with empty gross pay will be omitted.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4 bg-muted/10">
            {isLoadingStaff ? (
              <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
            ) : activeStaff.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground">No active staff found for this branch.</div>
            ) : (
              <div className="space-y-3">
                {activeStaff.map(s => (
                  <div key={s.id} className="grid grid-cols-[2fr_1fr_1fr_1fr] items-center gap-4 bg-background p-3 rounded-lg border">
                    <div>
                      <div className="font-medium text-sm">{s.name}</div>
                      <div className="text-xs text-muted-foreground capitalize">{s.role}</div>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wider block mb-1">Gross (₦)</label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        className="h-8"
                        value={items[s.id]?.gross || ""}
                        onChange={(e) => setItems(prev => ({ ...prev, [s.id]: { ...prev[s.id], gross: e.target.value } }))}
                      />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wider block mb-1">Deductions (₦)</label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        className="h-8"
                        value={items[s.id]?.deductions || ""}
                        onChange={(e) => setItems(prev => ({ ...prev, [s.id]: { ...prev[s.id], deductions: e.target.value } }))}
                      />
                    </div>
                    <div className="text-right">
                      <label className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wider block mb-1">Net Pay</label>
                      <div className="font-semibold text-sm h-8 flex items-center justify-end">
                        {formatCurrency((Number(items[s.id]?.gross || 0) - Number(items[s.id]?.deductions || 0)))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter className="px-6 py-4 border-t bg-background flex items-center justify-between sm:justify-between shrink-0">
            <div className="text-sm font-medium">
              Total Net: <span className="text-lg font-bold ml-2 text-primary">{formatCurrency(totalPreview)}</span>
            </div>
            <Button type="submit" disabled={createMut.isPending || activeStaff.length === 0} className="gap-2">
              {createMut.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Save Draft
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// Salary & Banks Tab
// -----------------------------------------------------------------------------
function SalaryBanksTab() {
  const { data: staffData, isLoading } = useQuery({
    queryKey: ["payroll-staff"],
    queryFn: () => fetchApi<{ staff: PayrollStaff[] }>("/api/payroll/staff"),
  });
  const staffList = staffData?.staff ?? [];

  const activeStaff = useMemo(() => {
    return (staffList || []).filter(s => s.isActive);
  }, [staffList]);

  return (
    <Card>
      <CardHeader className="border-b pb-4 mb-4">
        <CardTitle>Staff Salary & Bank Details</CardTitle>
        <CardDescription>Maintain banking info required for direct deposit.</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Staff Member</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Basic Salary</TableHead>
                <TableHead>Bank Info</TableHead>
                <TableHead className="w-[100px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {activeStaff.map((staff) => (
                <TableRow key={staff.id}>
                  <TableCell className="font-medium">{staff.name}</TableCell>
                  <TableCell className="capitalize text-muted-foreground">{staff.role}</TableCell>
                  <TableCell>
                    {staff.basicSalary != null ? formatCurrency(staff.basicSalary / 100) : <span className="text-muted-foreground text-xs italic">Not set</span>}
                  </TableCell>
                  <TableCell>
                    {staff.bankReady ? (
                      <div>
                        <div className="text-sm font-medium">{staff.bankName}</div>
                        <div className="text-xs text-muted-foreground">****{staff.accountLast4}</div>
                      </div>
                    ) : (
                      <span className="text-xs text-amber-600 flex items-center gap-1 bg-amber-500/10 px-2 py-1 rounded-md w-fit">
                        <AlertTriangle className="w-3 h-3" /> Setup required
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <EditBankDialog staff={staff} />
                  </TableCell>
                </TableRow>
              ))}
              {activeStaff.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                    No active staff found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Edit Bank Dialog
// -----------------------------------------------------------------------------
function EditBankDialog({ staff }: { staff: PayrollStaff }) {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: banksRes, isLoading: banksLoading } = useQuery({
    queryKey: ["payroll-banks"],
    queryFn: () => fetchApi<{ banks: Bank[] }>("/api/payroll/banks"),
    enabled: open,
    staleTime: Infinity,
  });

  const form = useForm<z.infer<typeof bankFormSchema>>({
    resolver: zodResolver(bankFormSchema),
    defaultValues: {
      bankCode: staff.bankCode || "",
      accountNumber: "",
      basicSalaryNaira: staff.basicSalary ? staff.basicSalary / 100 : 0,
    },
  });

  const updateMut = useMutation({
    mutationFn: (data: any) => fetchApi(`/api/payroll/staff/${staff.id}/bank`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data)
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["payroll-staff"] });
      toast({ title: "Bank details updated", description: `Saved successfully for ${staff.name}` });
      setOpen(false);
    },
    onError: (err: Error) => toast({ title: "Failed to update", description: err.message, variant: "destructive" }),
  });

  const onSubmit = (data: z.infer<typeof bankFormSchema>) => {
    const payload: any = {
      bankCode: data.bankCode,
      basicSalary: Math.round(data.basicSalaryNaira * 100),
    };
    if (data.accountNumber) {
      payload.accountNumber = data.accountNumber;
    }
    updateMut.mutate(payload);
  };

  return (
    <Dialog open={open} onOpenChange={(val) => {
      if (val) {
        form.reset({
          bankCode: staff.bankCode || "",
          accountNumber: "",
          basicSalaryNaira: staff.basicSalary ? staff.basicSalary / 100 : 0,
        });
      }
      setOpen(val);
    }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">Edit</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Update Staff Bank</DialogTitle>
          <DialogDescription>Set banking information for {staff.name}.</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField control={form.control} name="basicSalaryNaira" render={({ field }) => (
              <FormItem>
                <FormLabel>Basic Salary (₦)</FormLabel>
                <FormControl>
                  <Input type="number" step="0.01" min="0" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )} />

            <div className="border-t pt-4 mt-2">
              <h4 className="text-sm font-medium mb-3">Bank Details</h4>
              <FormField control={form.control} name="bankCode" render={({ field }) => (
                <FormItem className="mb-4">
                  <FormLabel>Bank Name</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value} disabled={banksLoading}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={banksLoading ? "Loading banks..." : "Select a bank"} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {banksRes?.banks.map(b => (
                        <SelectItem key={b.code} value={b.code}>{b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )} />

              <FormField control={form.control} name="accountNumber" render={({ field }) => (
                <FormItem>
                  <FormLabel>Account Number</FormLabel>
                  <FormControl>
                    <Input placeholder="10-digit account number" maxLength={10} {...field} />
                  </FormControl>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    {staff.accountLast4 ? `Current account ends in ****${staff.accountLast4}. Enter full new number to change.` : "Enter full 10-digit NUBAN."}
                  </p>
                  <FormMessage />
                </FormItem>
              )} />
            </div>

            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={updateMut.isPending}>
                {updateMut.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
